/**
 * Home — the dashboard's primary screen.
 *
 * Above the fold: setup-verified banner (if needed), revenue hero card
 * with sparkline + CountUp, +Create payment link CTA, quick stats grid.
 * Below: recent payment links.
 *
 * Stats are computed client-side from the last 100 invoices. Move to a
 * server aggregate (`/api/admin/dashboard/overview`) when volume justifies
 * it (V1.5 — see SPEC §7.1).
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ApiError,
  createInvoiceAdmin,
  getMyMerchant,
  listInvoicesAdmin,
  listUnattributedDeposits,
  matchUnattributedDeposit,
  ignoreUnattributedDeposit,
  type UnattributedDeposit,
  type Invoice,
  type MerchantProfile,
} from "../lib/api";
import { Banner, Btn, CountUp, Pill, Sparkline } from "../components/ui";
import { Ic } from "../components/Icons";
import { useT } from "../lib/i18n";

const day = 86_400_000;

/**
 * Build a LeoDex deeplink for the merchant's ZEC → USDC conversion CTA.
 * Pre-fills source asset, destination asset, and affiliate code. If the
 * merchant has set a preferred conversion EVM address, that pre-fills
 * the destination address field too (saving them one paste).
 */
const leodexConvertUrl = (preferredAddress: string | null): string => {
  const params = new URLSearchParams({
    from: "ZEC.ZEC",
    to: "BASE.USDC-0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
    affiliate: "leo",
  });
  if (preferredAddress && /^0x[0-9a-fA-F]{40}$/.test(preferredAddress)) {
    params.set("destination", preferredAddress);
  }
  return `https://leodex.io/swap?${params.toString()}`;
};

interface Stats {
  total: number;
  paid: number;
  last7d: number; // cents
  last30d: number; // cents
  allTime: number; // cents
}

const computeStats = (invoices: Invoice[]): Stats => {
  const now = Date.now();
  let paid = 0;
  let allTime = 0;
  let last7d = 0;
  let last30d = 0;
  for (const inv of invoices) {
    if (inv.status === "paid") {
      paid++;
      allTime += inv.amount_usd_cents;
      const age = now - (inv.paid_at ?? inv.created_at);
      if (age <= 7 * day) last7d += inv.amount_usd_cents;
      if (age <= 30 * day) last30d += inv.amount_usd_cents;
    }
  }
  return { total: invoices.length, paid, last7d, last30d, allTime };
};

/** 7-day daily totals as a series for the sparkline. */
const sparkSeries = (invoices: Invoice[]): number[] => {
  const buckets: number[] = Array.from({ length: 7 }, () => 0);
  const now = Date.now();
  for (const inv of invoices) {
    if (inv.status !== "paid") continue;
    const t = inv.paid_at ?? inv.created_at;
    const ageDays = Math.floor((now - t) / day);
    if (ageDays < 7) buckets[6 - ageDays]! += inv.amount_usd_cents / 100;
  }
  return buckets;
};

const truncMid = (s: string, head = 6, tail = 4): string =>
  s.length <= head + tail + 1 ? s : `${s.slice(0, head)}…${s.slice(-tail)}`;

const formatRelative = (ts: number, t: (key: string) => string): string => {
  const delta = Date.now() - ts;
  const min = 60_000;
  const hour = 3_600_000;
  if (delta < min) return t("home.just_now");
  if (delta < hour) return `${Math.floor(delta / min)}${t("home.minutes_ago")}`;
  if (delta < day) return `${Math.floor(delta / hour)}${t("home.hours_ago")}`;
  return `${Math.floor(delta / day)}${t("home.days_ago")}`;
};

export function Home() {
  const t = useT();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [unattributed, setUnattributed] = useState<UnattributedDeposit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creatingOpen, setCreatingOpen] = useState(false);

  const refreshUnattributed = async () => {
    try {
      const r = await listUnattributedDeposits();
      setUnattributed(r.data);
    } catch {
      /* best-effort — don't block home render on this */
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, inv, un] = await Promise.all([
          getMyMerchant(),
          listInvoicesAdmin({ limit: 100 }),
          listUnattributedDeposits().catch(() => ({ object: "list" as const, data: [] })),
        ]);
        if (cancelled) return;
        setMerchant(m);
        setInvoices(inv.data);
        setUnattributed(un.data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(
    () => (invoices ? computeStats(invoices) : null),
    [invoices],
  );
  const series = useMemo(
    () => (invoices ? sparkSeries(invoices) : []),
    [invoices],
  );
  const recent = invoices?.slice(0, 5) ?? [];

  return (
    <div className="scroll">
      <div className="pad fade-in">
        {merchant && !merchant.setup_verified && (
          <Banner tone="warn">
            {t("home.banner_verify_prefix")}{" "}
            {/* Verification is the last step of the /setup wizard; Settings has
                no control for it, so linking there was a dead end. */}
            <Link to="/setup" className="b-act">
              {t("home.setup")}
            </Link>{" "}
            {t("home.banner_verify_suffix")}
          </Banner>
        )}

        {error && <Banner tone="fail">{error}</Banner>}

        <div className="revcard">
          <div className="rl">
            <Ic name="zap" size={14} /> {t("home.this_week")}
          </div>
          <div className="rnum tnum">
            <CountUp value={(stats?.last7d ?? 0) / 100} />
          </div>
          <div className="rsub">
            {stats ? (
              <>
                {stats.paid} {t("home.paid")} · {stats.total}{" "}
                {t("home.total_period")}
              </>
            ) : (
              t("home.loading")
            )}
          </div>
          {series.length === 7 && stats && stats.paid > 0 && (
            <Sparkline data={series} />
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          <Btn
            block
            onClick={() => setCreatingOpen(true)}
            className="home-cta"
          >
            <Ic name="plus" size={18} /> {t("home.create_payment_link")}
          </Btn>
        </div>

        <div className="s-label">{t("home.quick_stats")}</div>
        <div className="stat-grid">
          <StatTile label={t("home.last_30_days")} value={`$${((stats?.last30d ?? 0) / 100).toFixed(2)}`} />
          <StatTile label={t("home.all_time_paid")} value={`$${((stats?.allTime ?? 0) / 100).toFixed(2)}`} />
          <StatTile
            label={t("home.receive_to")}
            value={receiveToLabel(merchant, t)}
            small
          />
          <StatTile
            label={t("home.notifications")}
            value={merchant?.telegram_dm_enabled !== false ? `${t("home.telegram_dm")} ✓` : t("home.off")}
            small
          />
        </div>

        {unattributed.length > 0 && (
          <UnattributedPanel
            deposits={unattributed}
            onResolved={() => void refreshUnattributed()}
          />
        )}

        {merchant?.chains && merchant.chains.length > 0 && (
          <>
            <div className="s-label">{t("home.revenue_by_chain")}</div>
            <div className="card">
              {chainBreakdown(invoices, merchant.chains).map((row) => (
                <div key={row.chain_id} className="li" style={{ cursor: "default" }}>
                  <div
                    className="li-ic"
                    style={{ background: chainColor(row.chain_id), color: "#fff" }}
                  >
                    <span style={{ fontWeight: 700, fontSize: 11 }}>
                      {chainAbbr(row.chain_id)}
                    </span>
                  </div>
                  <div className="li-main">
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      {chainLabel(row.chain_id)}
                    </div>
                    <div className="li-meta">
                      {row.paid_count} {t("home.paid")} · {row.pending_count}{" "}
                      {t("home.pending")}
                    </div>
                  </div>
                  <div className="li-right">
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      ${(row.paid_usd_cents / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {merchant?.zcash?.has_sapling && (
          <a
            className="card card-pad"
            href={leodexConvertUrl(merchant.preferred_conversion_address ?? null)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              textDecoration: "none",
              color: "inherit",
              marginTop: 14,
              transition: "transform .15s var(--ease, ease)",
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                background: "var(--brand-50)",
                color: "var(--brand-600)",
                display: "grid",
                placeItems: "center",
                flex: "none",
              }}
            >
              <Ic name="zap" size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
                {t("home.convert_prefix")} ZEC {t("home.convert_to")} USDC
              </div>
              <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>
                {t("home.convert_subtitle")}
              </div>
            </div>
            <Ic name="ext" size={16} />
          </a>
        )}

        <div className="s-label">
          {t("home.recent_payment_links")}
          <Link
            to="/activity"
            style={{ fontSize: 12, color: "var(--brand-700)" }}
          >
            {t("home.see_all")}
          </Link>
        </div>
        <div className="card">
          {invoices === null ? (
            <div className="card-pad">
              <div className="muted" style={{ color: "var(--ink-3)" }}>
                {t("home.loading_ellipsis")}
              </div>
            </div>
          ) : recent.length === 0 ? (
            <div className="empty">
              <div className="empty-vis">
                <div
                  style={{
                    width: 64,
                    height: 64,
                    background: "var(--brand-50)",
                    border: "1px solid var(--brand-100)",
                    borderRadius: 12,
                    margin: "8px auto",
                    display: "grid",
                    placeItems: "center",
                    color: "var(--brand-600)",
                  }}
                >
                  <Ic name="link" size={28} />
                </div>
              </div>
              <h4>{t("home.empty_title")}</h4>
              <p>
                {t("home.empty_tap")}{" "}
                <strong>+ {t("home.create_payment_link")}</strong>{" "}
                {t("home.empty_body")}
              </p>
              <Btn onClick={() => setCreatingOpen(true)}>
                <Ic name="plus" size={16} /> {t("home.create_first_link")}
              </Btn>
            </div>
          ) : (
            recent.map((inv) => (
              <div
                key={inv.id}
                className="li"
                onClick={() =>
                  void navigate({
                    to: "/activity/$id",
                    params: { id: inv.id },
                  })
                }
                role="button"
                tabIndex={0}
              >
                <div className={`li-ic ${pillCls(inv.status)}`}>
                  <Ic
                    name={
                      inv.status === "paid"
                        ? "check"
                        : inv.status === "failed"
                          ? "x"
                          : inv.status === "expired"
                            ? "clock"
                            : "link"
                    }
                    size={18}
                  />
                </div>
                <div className="li-main">
                  <div className="li-amt tnum">
                    ${(inv.amount_usd_cents / 100).toFixed(2)}
                  </div>
                  {inv.memo && <div className="li-memo">{inv.memo}</div>}
                  <div className="li-meta">{truncMid(inv.id)}</div>
                </div>
                <div className="li-right">
                  <Pill status={inv.status} />
                  <div className="li-time">{formatRelative(inv.created_at, t)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {creatingOpen && (
        <CreateLinkSheet
          onClose={() => setCreatingOpen(false)}
          chains={merchant?.chains ?? []}
          onCreated={() => {
            // Refetch when the modal dismisses.
            setInvoices(null);
            void listInvoicesAdmin({ limit: 100 }).then((r) =>
              setInvoices(r.data),
            );
          }}
        />
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  small,
}: {
  label: string;
  value: string;
  small?: boolean;
}) {
  return (
    <div className="card stat">
      <div className="sl">{label}</div>
      <div className={`sv tnum${small ? " sm" : ""}`}>{value}</div>
    </div>
  );
}

interface ChainRow {
  chain_id: string;
  paid_count: number;
  pending_count: number;
  paid_usd_cents: number;
}

/** Aggregate invoices by chain. Falls back to "ZEC" for legacy forwarder
 *  invoices that don't have payment_chain_id set yet. */
const chainBreakdown = (
  invoices: Invoice[] | null,
  configuredChains: Array<{ chain_id: string }>,
): ChainRow[] => {
  if (!invoices) return configuredChains.map((c) => zeroRow(c.chain_id));
  const map = new Map<string, ChainRow>();
  for (const c of configuredChains) map.set(c.chain_id, zeroRow(c.chain_id));
  for (const inv of invoices) {
    // V1.4.4: for paid invoices, attribute revenue to the chain the buyer
    // ACTUALLY paid on (paid_on.chain_id, computed server-side). Falls
    // back to legacy payment_chain_id for pending invoices and legacy
    // pre-V1.1 paid invoices.
    const chain =
      (inv.status === "paid" ? inv.paid_on?.chain_id : null) ??
      inv.payment_chain_id ??
      (inv.zcash?.u1_address ? "ZEC" : null);
    if (!chain) continue;
    let row = map.get(chain);
    if (!row) {
      row = zeroRow(chain);
      map.set(chain, row);
    }
    if (inv.status === "paid") {
      row.paid_count++;
      row.paid_usd_cents += inv.amount_usd_cents;
    } else if (inv.status === "pending") {
      row.pending_count++;
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => b.paid_usd_cents - a.paid_usd_cents,
  );
};

const zeroRow = (chain_id: string): ChainRow => ({
  chain_id,
  paid_count: 0,
  pending_count: 0,
  paid_usd_cents: 0,
});

/**
 * "Receive to" tile label. Multichain merchants (the common case after
 * V1.1) get a "Multichain · N" summary instead of the old Zcash-first
 * heuristic that wrongly labelled a 7-chain merchant as "Your Zcash
 * wallet". Pure single-chain merchants (only ZEC or only one EVM)
 * still get the specific label.
 */
const receiveToLabel = (
  merchant: MerchantProfile | null,
  t: (key: string) => string,
): string => {
  if (!merchant) return "—";
  const chains = merchant.chains ?? [];
  const enabledChains = chains.length;
  if (enabledChains > 1) return `Multichain · ${enabledChains}`;
  if (merchant.zcash?.has_sapling) return `${t("home.your")} Zcash ${t("home.wallet")}`;
  if (chains[0]) return chainLabel(chains[0].chain_id);
  if (merchant.default_settlement) {
    return `${merchant.default_settlement.chain} USDC`;
  }
  return "—";
};

const chainLabel = (id: string): string => {
  switch (id) {
    case "ZEC":
      return "Zcash";
    case "BTC":
      return "Bitcoin";
    case "BASE":
      return "USDC on Base";
    case "ARB":
      return "USDC on Arbitrum";
    case "OP":
      return "USDC on Optimism";
    case "POLY":
      return "USDC on Polygon";
    case "ETH":
      return "USDC on Ethereum";
    default:
      return id;
  }
};
const chainAbbr = (id: string): string =>
  id === "BASE" ? "B"
  : id === "ARB" ? "A"
  : id === "POLY" ? "P"
  : id === "ETH" ? "E"
  : id === "OP" ? "O"
  : id === "BTC" ? "₿"
  : id === "ZEC" ? "Z"
  : "?";
const chainColor = (id: string): string => {
  switch (id) {
    case "ZEC":
      return "#08a072";
    case "BTC":
      return "#f7931a";
    case "BASE":
      return "#0052ff";
    case "ARB":
      return "#28a0f0";
    case "OP":
      return "#ff0420";
    case "POLY":
      return "#8247e5";
    case "ETH":
      return "#627eea";
    default:
      return "#7b8794";
  }
};

function pillCls(status: Invoice["status"]): string {
  return status === "paid"
    ? "ok"
    : status === "failed"
      ? "fail"
      : status === "expired"
        ? "muted"
        : "warn";
}

// ─── Create-link sheet + result modal ─────────────────────────────────

function CreateLinkSheet({
  onClose,
  onCreated,
  chains,
}: {
  onClose: () => void;
  onCreated: () => void;
  chains: Array<{ chain_id: string }>;
}) {
  const t = useT();
  const [amountStr, setAmountStr] = useState("");
  const [memo, setMemo] = useState("");
  // Default to ZEC if configured, else first chain.
  const defaultChain =
    chains.find((c) => c.chain_id === "ZEC")?.chain_id ?? chains[0]?.chain_id ?? "ZEC";
  const [paymentChain, setPaymentChain] = useState<string>(defaultChain);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Invoice | null>(null);
  const [copied, setCopied] = useState(false);

  const parseAmountCents = (s: string): number | null => {
    const trimmed = s.trim();
    if (!trimmed) return null;
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
    const cents = Math.round(parseFloat(trimmed) * 100);
    if (cents < 100 || cents > 10_000_000) return null;
    return cents;
  };

  const handleCreate = async () => {
    const cents = parseAmountCents(amountStr);
    if (cents == null) {
      setError(t("home.amount_range_error"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const inv = await createInvoiceAdmin({
        amount_usd_cents: cents,
        memo: memo.trim() || null,
        payment_chain: paymentChain,
      });
      setCreated(inv);
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.pay_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleShare = () => {
    if (!created) return;
    const url = encodeURIComponent(created.pay_url);
    const text = encodeURIComponent(
      `${t("home.share_pay")} $${(created.amount_usd_cents / 100).toFixed(2)}${
        created.memo ? ` ${t("home.share_for")} ${created.memo}` : ""
      } ${t("home.share_via")} Shieldz`,
    );
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, "_blank");
  };

  return (
    <div className="overlay show" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet">
        <div className="sheet-grab" />
        <div className="sheet-head">
          <h3>{created ? t("home.payment_link_ready") : t("home.create_payment_link")}</h3>
          <button
            className="sheet-x"
            onClick={onClose}
            aria-label={t("home.close")}
          >
            <Ic name="x" size={16} />
          </button>
        </div>
        <div className="sheet-body">
          {created ? (
            <>
              <div className="result-hero">
                <div className="result-check">
                  <Ic name="check" size={28} />
                </div>
                <h3>
                  ${(created.amount_usd_cents / 100).toFixed(2)}
                  {created.memo ? ` · ${created.memo}` : ""}
                </h3>
                <p>{t("home.share_with_customer")}</p>
                {created.payment_targets && created.payment_targets.length > 1 && (
                  <p
                    style={{
                      marginTop: 6,
                      fontSize: 12.5,
                      color: "var(--ink-3)",
                    }}
                  >
                    {t("home.buyer_can_pay_on")}{" "}
                    {created.payment_targets
                      .map((t) => chainLabel(t.chain_id))
                      .join(" · ")}
                  </p>
                )}
              </div>
              <div style={{ marginTop: 18 }}>
                <div className="field">
                  <label>{t("home.pay_url")}</label>
                  <div className="copyf">
                    <a
                      className="val"
                      href={created.pay_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t("home.open_invoice_page_title")}
                      style={{ color: "inherit", textDecoration: "none", wordBreak: "break-all" }}
                    >
                      {created.pay_url}
                    </a>
                    <button
                      className={copied ? "copied" : ""}
                      onClick={() => void handleCopy()}
                      type="button"
                    >
                      {copied ? (
                        <>
                          <Ic name="check" size={14} /> {t("home.copied")}
                        </>
                      ) : (
                        <>
                          <Ic name="copy" size={14} /> {t("home.copy")}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <Btn block onClick={handleShare}>
                  <Ic name="share" size={16} /> {t("home.share_via_telegram")}
                </Btn>
                <a
                  className="btn btn-secondary"
                  href={created.pay_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ flex: "none", display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}
                >
                  <Ic name="ext" size={16} /> {t("home.open_invoice")}
                </a>
              </div>
              <div className="hint" style={{ marginTop: 14 }}>
                <Ic name="bell" size={16} />
                {t("home.dm_hint")}
              </div>
              <Btn
                variant="ghost"
                block
                onClick={() => {
                  setCreated(null);
                  setAmountStr("");
                  setMemo("");
                }}
                style={{ marginTop: 8 }}
              >
                {t("home.create_another")}
              </Btn>
            </>
          ) : (
            <>
              {error && (
                <Banner tone="fail">{error}</Banner>
              )}
              <div className="field" style={{ marginTop: 8 }}>
                <label htmlFor="amount">{t("home.amount")}</label>
                <div className="input-money">
                  <span className="cur">$</span>
                  <input
                    id="amount"
                    className="input"
                    type="text"
                    inputMode="decimal"
                    value={amountStr}
                    onChange={(e) => setAmountStr(e.target.value)}
                    placeholder="20.00"
                    autoFocus
                  />
                </div>
              </div>
              {chains.length > 1 && (
                <div className="field" style={{ marginTop: 14 }}>
                  <label htmlFor="chain">
                    {t("home.show_first_to_buyer")}{" "}
                    <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>
                      {t("home.they_can_switch")}
                    </span>
                  </label>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                    }}
                  >
                    {chains.map((c) => {
                      const sel = c.chain_id === paymentChain;
                      return (
                        <button
                          key={c.chain_id}
                          type="button"
                          onClick={() => setPaymentChain(c.chain_id)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "8px 12px",
                            borderRadius: 999,
                            border: sel
                              ? `2px solid ${chainColor(c.chain_id)}`
                              : "1px solid var(--line-2)",
                            background: sel
                              ? "var(--brand-50)"
                              : "var(--surface)",
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: sel ? "var(--ink)" : "var(--ink-2)",
                            cursor: "pointer",
                            fontFamily: "inherit",
                          }}
                        >
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: 4,
                              background: chainColor(c.chain_id),
                              color: "#fff",
                              display: "grid",
                              placeItems: "center",
                              fontSize: 8,
                              fontWeight: 700,
                            }}
                          >
                            {chainAbbr(c.chain_id)}
                          </span>
                          {chainLabel(c.chain_id)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="field" style={{ marginTop: 14 }}>
                <label htmlFor="memo">{t("home.whats_it_for")} <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>{t("home.optional")}</span></label>
                <input
                  id="memo"
                  className="input"
                  type="text"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder={t("home.memo_placeholder")}
                  maxLength={120}
                />
              </div>
              <Btn
                block
                onClick={() => void handleCreate()}
                loading={busy}
                disabled={busy || !amountStr}
                style={{ marginTop: 18 }}
              >
                {t("home.create_payment_link")}
              </Btn>
              <Btn variant="ghost" block onClick={onClose} style={{ marginTop: 6 }}>
                {t("home.cancel")}
              </Btn>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── V1.4.5 unattributed deposits panel ───────────────────────────────────
// Surfaces deposits the watcher saw to the merchant's address but couldn't
// match to an open invoice's exact amount. Each row offers one-click
// "Match to invoice X" (if the backend had a candidate) and "Ignore".
// Refund is documented but not wired (merchant does it from their wallet).

function UnattributedPanel({
  deposits,
  onResolved,
}: {
  deposits: UnattributedDeposit[];
  onResolved: () => void;
}) {
  const t = useT();
  const [busyId, setBusyId] = useState<number | null>(null);

  const handleMatch = async (d: UnattributedDeposit) => {
    if (!d.candidate) return;
    setBusyId(d.id);
    try {
      await matchUnattributedDeposit(d.id, d.candidate.invoice_id);
      onResolved();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleIgnore = async (d: UnattributedDeposit, action: "misc" | "ignored") => {
    setBusyId(d.id);
    try {
      await ignoreUnattributedDeposit(d.id, action);
      onResolved();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  // Human-readable atomic-amount rendering. ERC-20 USDC/USDT and BTC use 6/8
  // decimals respectively; we mirror the chain-id heuristic from elsewhere
  // in the dashboard.
  const formatAmount = (atomic: string, chain_id: string, asset_symbol: string | null): string => {
    const decimals =
      asset_symbol === "USDC" || asset_symbol === "USDT" ? 6
      : chain_id.toUpperCase() === "BTC" ? 8
      : chain_id.toUpperCase() === "ZEC" ? 8
      : 6;
    try {
      const big = BigInt(atomic);
      const div = 10n ** BigInt(decimals);
      const whole = big / div;
      const frac = big % div;
      const fracStr = frac.toString().padStart(decimals, "0").replace(/0+$/, "");
      return fracStr.length > 0 ? `${whole}.${fracStr}` : whole.toString();
    } catch {
      return atomic;
    }
  };

  const chainBadge = (chain_id: string, asset_symbol: string | null): string => {
    const c = chain_id.toUpperCase();
    if (asset_symbol === "USDT") return `${chainLabel(c).replace(" USDC", "")} · USDT`;
    return chainLabel(c);
  };

  return (
    <>
      <div className="s-label" style={{ color: "var(--warn-700, #b45309)" }}>
        {t("home.needs_review")} · {deposits.length}
      </div>
      <div
        className="card"
        style={{
          border: "1px solid #f59e0b40",
          background: "linear-gradient(180deg, #fffbeb, var(--surface-1))",
        }}
      >
        {deposits.map((d) => {
          const human = formatAmount(d.amount_atomic, d.chain_id, d.asset_symbol);
          const tickerLabel =
            d.asset_symbol ?? d.chain_id.toUpperCase();
          return (
            <div
              key={d.id}
              className="li"
              style={{
                cursor: "default",
                display: "flex",
                alignItems: "stretch",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div className="li-main" style={{ minWidth: 220 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {human} {tickerLabel} {t("home.on")} {chainBadge(d.chain_id, d.asset_symbol)}
                </div>
                <div className="li-meta" style={{ fontSize: 12.5 }}>
                  {d.candidate ? (
                    <>
                      {t("home.looks_like_invoice")}{" "}
                      <code className="mono">{d.candidate.invoice_public_id}</code>{" "}
                      ({t("home.asked")} ${(d.candidate.amount_usd_cents / 100).toFixed(2)})
                    </>
                  ) : (
                    <>{t("home.no_matching_invoice")}</>
                  )}
                  {" · "}
                  <span className="mono">{d.tx_hash.slice(0, 10)}…</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {d.candidate && (
                  <Btn
                    variant="primary"
                    onClick={() => void handleMatch(d)}
                    loading={busyId === d.id}
                  >
                    {t("home.match_to_invoice")}
                  </Btn>
                )}
                <Btn
                  variant="secondary"
                  onClick={() => void handleIgnore(d, "misc")}
                  disabled={busyId === d.id}
                >
                  {t("home.keep_as_misc")}
                </Btn>
                <Btn
                  variant="ghost"
                  onClick={() => void handleIgnore(d, "ignored")}
                  disabled={busyId === d.id}
                >
                  {t("home.ignore")}
                </Btn>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
